import React from "react";
import Link from "next/link";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import CURRENCIES from "currencies.json/currencies.json";
import { CandleLightingEvent, HavdalahEvent, HebrewCalendar, Location, ParshaEvent } from "@hebcal/core";
import "@hebcal/locales";
import tzlookup from "tz-lookup";
import {HotelsList, KosherPlacesGrid} from "../ui-components";

const tourismStyles = `
.tr-tour__title {
  font-size: clamp(20px, 3.5vw, 32px);
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin: 64px 0 12px;
}
.tr-tour__subtitle {
  text-align: center;
  font-size: clamp(18px, 2.5vw, 24px);
  color: #1a1a2e;
  margin: 0 0 36px;
  margin-top:40px;
  font-weight: 400;
}
.tr-tour__subtitle strong {
  font-weight: 700;
}

/* ── Grille cartes info ── */
.tr-tour__cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 20px;
  max-width: 960px;
  margin: 0 auto 48px;
  align-items: stretch;
}
.tr-tour__card--time   { grid-column: 1; grid-row: 1; }
.tr-tour__card--devise { grid-column: 1; grid-row: 2; }
.tr-tour__card--shabbat { grid-column: 2; grid-row: 1 / span 2; }
.tr-tour__card {
  border-radius: 20px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}
.tr-tour__card--time,
.tr-tour__card--devise {
  flex-direction: row;
  align-items: center;
  gap: 16px;
}
.tr-tour__card--time {
  background: #e6e6f0;
}
.tr-tour__card--devise {
  background: #d9f3e4;
}
.tr-tour__card--shabbat {
  background: #dbeaff;
}
.tr-tour__card-icon {
  color: var(--primary, #1b579e);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255,255,255,0.55);
  flex-shrink: 0;
}
.tr-tour__card-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tr-tour__card-label {
  font-size: 13px;
  color: #374151;
}
.tr-tour__card-value {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.25;
}

/* Shabbat card — layout à deux colonnes */
.tr-tour__shabbat {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 18px;
  align-items: start;
}
.tr-tour__shabbat-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tr-tour__shabbat-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}
.tr-tour__shabbat-paracha {
  font-size: 15px;
  font-weight: 600;
  color: var(--secondary, #38b6ff);
  line-height: 1.3;
}
.tr-tour__shabbat-dates {
  font-size: 13px;
  color: #6b7280;
}
.tr-tour__shabbat-right {
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: right;
}
.tr-tour__shabbat-time-label {
  font-size: 12px;
  color: #374151;
}
.tr-tour__shabbat-time-value {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
}
.tr-tour__shabbat-link {
  grid-column: 1 / -1;
  text-align: center;
  font-size: 13px;
  color: var(--primary, #1b579e);
  text-decoration: underline;
  margin-top: 6px;
  font-weight: 500;
}

/* ── Section contenu ── */
.tr-tour__section {
  max-width: 820px;
  margin: 0 auto 40px;
  padding: 0 16px;
}
.tr-tour__section-title {
  font-size: clamp(18px, 2.5vw, 24px);
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 12px;
}
.tr-tour__section-intro {
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  margin: 0 0 6px;
}
.tr-tour__section-link {
  display: inline-block;
  font-size: 14px;
  color: var(--primary, #1b579e);
  text-decoration: underline;
  font-weight: 500;
}

/* ── Eruv badge ── */
.tr-tour__eruv {
  max-width: 820px;
  margin: 0 auto 40px;
  padding: 16px 20px;
  border-radius: 14px;
  background: #f5f7fb;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tr-tour__eruv-head {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
}
.tr-tour__eruv-pill {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
}
.tr-tour__eruv-pill--yes { background: #dcfce7; color: #15803d; }
.tr-tour__eruv-pill--no { background: #fee2e2; color: #b91c1c; }
.tr-tour__eruv-pill--partial { background: #fef3c7; color: #b45309; }
.tr-tour__eruv-comment {
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
}

/* ── Rich content (tourismInfos) ── */
.tr-tour__rich {
  max-width: 820px;
  margin: 0 auto 60px;
  padding: 0 16px;
  font-size: 15px;
  color: #1f2937;
  line-height: 1.7;
}
.tr-tour__rich h1,
.tr-tour__rich h2,
.tr-tour__rich h3 {
  color: #1a1a2e;
  margin-top: 28px;
  margin-bottom: 10px;
}
.tr-tour__rich p { margin: 0 0 12px; }
.tr-tour__rich a { color: var(--primary, #1b579e); text-decoration: underline; }
.tr-tour__rich ul, .tr-tour__rich ol { padding-left: 22px; margin: 0 0 14px; }

@media (max-width: 768px) {
  .tr-tour__cards {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    padding: 0 16px;
  }
  .tr-tour__card--time   { grid-column: 1; grid-row: 1; }
  .tr-tour__card--devise { grid-column: 2; grid-row: 1; }
  .tr-tour__card--shabbat { grid-column: 1 / span 2; grid-row: 2; }
  .tr-tour__title,
  .tr-tour__subtitle { padding: 0 16px; }
}
`;

/* ──────────────────────────────────────────────────────────
   Helpers devise + heure locale via Nominatim (reverse geocoding)
   ────────────────────────────────────────────────────────── */

function findCurrencyInfo(code) {
    if (!code) return null;
    return CURRENCIES.find(c => c.code === code) || null;
}

function formatTime(timezone) {
    if (!timezone) return null;
    try {
        return new Intl.DateTimeFormat("fr", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: timezone,
        }).format(new Date());
    } catch {
        return null;
    }
}

// Cache localStorage : les pays/timezones ne changent pas → TTL 30 jours.
// Clé basée sur les coords arrondies à 3 décimales (~110m) pour mutualiser.
const GEO_CACHE_TTL = 30 * 24 * 60 * 60 * 1000;

function geoCacheKey(lat, lon) {
    return `tr_geo_${Number(lat).toFixed(3)}_${Number(lon).toFixed(3)}`;
}

function readGeoCache(lat, lon) {
    try {
        if (typeof window === "undefined") return null;
        const raw = window.localStorage.getItem(geoCacheKey(lat, lon));
        if (!raw) return null;
        const { data, ts } = JSON.parse(raw);
        if (!ts || Date.now() - ts > GEO_CACHE_TTL) return null;
        return data;
    } catch {
        return null;
    }
}

function writeGeoCache(lat, lon, data) {
    try {
        if (typeof window === "undefined") return;
        window.localStorage.setItem(
            geoCacheKey(lat, lon),
            JSON.stringify({ data, ts: Date.now() })
        );
    } catch {
        // quota plein ou storage indisponible : on ignore silencieusement
    }
}

async function fetchGeoInfo(lat, lon) {
    if (lat == null || lon == null) return null;

    const cached = readGeoCache(lat, lon);
    if (cached) return cached;

    try {
        const [nominatimRes, tzRes] = await Promise.all([
            fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=jsonv2`,
                { headers: { "Accept-Language": "en" } }
            ),
            fetch(
                `https://timeapi.io/api/timezone/coordinate?latitude=${lat}&longitude=${lon}`
            ),
        ]);
        const nominatim = await nominatimRes.json();
        const tz = await tzRes.json();
        const data = {
            countryCode: nominatim.address?.country_code?.toUpperCase() || null,
            timezone: tz.timeZone || null,
        };
        writeGeoCache(lat, lon, data);
        return data;
    } catch {
        return null;
    }
}

function getNextShabbat(lat, lon, israel = false) {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 1);
    const end = new Date(now);
    end.setDate(end.getDate() + 10);

    const events = HebrewCalendar.calendar({
        start,
        end,
        candlelighting: true,
        sedrot: true,
        locale: "fr",
        location: new Location(parseFloat(lat), parseFloat(lon)),
        il: !!israel,
        noModern: true,
    });

    let entree = null, sortie = null, parasha = null, fridayDate = null;

    for (const ev of events) {
        if (ev instanceof CandleLightingEvent && ev.eventTime > now && !entree) {
            entree = ev.eventTime;
            fridayDate = ev.getDate().greg();
        }
        if (ev instanceof HavdalahEvent && entree && !sortie) {
            sortie = ev.eventTime;
        }
        if (ev instanceof ParshaEvent && entree && !parasha) {
            parasha = ev.parsha.join("-");
        }
        if (entree && sortie && parasha) break;
    }

    if (!entree) return null;

    const satDate = new Date(entree);
    satDate.setDate(satDate.getDate() + 1);

    // Fuseau IANA de l'org déduit des coordonnées (offline, synchrone). Sert de
    // fuseau de référence pour les heures ET les dates de Chabbat, sinon décalage
    // pour toute org à l'étranger (heure murale extraite du fuseau serveur).
    let orgTz = null;
    try {
        const la = parseFloat(lat), lo = parseFloat(lon);
        if (!isNaN(la) && !isNaN(lo)) orgTz = tzlookup(la, lo);
    } catch (e) { orgTz = null; }

    const fmt = (d, tz) => {
        const zone = tz || orgTz;
        return zone
            ? new Intl.DateTimeFormat("fr", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: zone }).format(d)
            : d.toTimeString().slice(0, 5);
    };

    const fmtDate = (d) =>
        new Intl.DateTimeFormat("fr", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: orgTz || undefined }).format(d);

    return { entree, sortie, parasha, fridayDate, satDate, fmt, fmtDate };
}

const CURRENCY_BY_COUNTRY = {
    AD:"EUR",AE:"AED",AF:"AFN",AG:"XCD",AI:"XCD",AL:"ALL",AM:"AMD",AO:"AOA",AR:"ARS",AS:"USD",
    AT:"EUR",AU:"AUD",AW:"AWG",AX:"EUR",AZ:"AZN",BA:"BAM",BB:"BBD",BD:"BDT",BE:"EUR",BF:"XOF",
    BG:"BGN",BH:"BHD",BI:"BIF",BJ:"XOF",BL:"EUR",BM:"BMD",BN:"BND",BO:"BOB",BQ:"USD",BR:"BRL",
    BS:"BSD",BT:"BTN",BV:"NOK",BW:"BWP",BY:"BYN",BZ:"BZD",CA:"CAD",CC:"AUD",CD:"CDF",CF:"XAF",
    CG:"XAF",CH:"CHF",CI:"XOF",CK:"NZD",CL:"CLP",CM:"XAF",CN:"CNY",CO:"COP",CR:"CRC",CU:"CUP",
    CV:"CVE",CW:"ANG",CX:"AUD",CY:"EUR",CZ:"CZK",DE:"EUR",DJ:"DJF",DK:"DKK",DM:"XCD",DO:"DOP",
    DZ:"DZD",EC:"USD",EE:"EUR",EG:"EGP",EH:"MAD",ER:"ERN",ES:"EUR",ET:"ETB",FI:"EUR",FJ:"FJD",
    FK:"FKP",FM:"USD",FO:"DKK",FR:"EUR",GA:"XAF",GB:"GBP",GD:"XCD",GE:"GEL",GF:"EUR",GG:"GBP",
    GH:"GHS",GI:"GIP",GL:"DKK",GM:"GMD",GN:"GNF",GP:"EUR",GQ:"XAF",GR:"EUR",GS:"GBP",GT:"GTQ",
    GU:"USD",GW:"XOF",GY:"GYD",HK:"HKD",HM:"AUD",HN:"HNL",HR:"EUR",HT:"HTG",HU:"HUF",ID:"IDR",
    IE:"EUR",IL:"ILS",IM:"GBP",IN:"INR",IO:"USD",IQ:"IQD",IR:"IRR",IS:"ISK",IT:"EUR",JE:"GBP",
    JM:"JMD",JO:"JOD",JP:"JPY",KE:"KES",KG:"KGS",KH:"KHR",KI:"AUD",KM:"KMF",KN:"XCD",KP:"KPW",
    KR:"KRW",KW:"KWD",KY:"KYD",KZ:"KZT",LA:"LAK",LB:"LBP",LC:"XCD",LI:"CHF",LK:"LKR",LR:"LRD",
    LS:"LSL",LT:"EUR",LU:"EUR",LV:"EUR",LY:"LYD",MA:"MAD",MC:"EUR",MD:"MDL",ME:"EUR",MF:"EUR",
    MG:"MGA",MH:"USD",MK:"MKD",ML:"XOF",MM:"MMK",MN:"MNT",MO:"MOP",MP:"USD",MQ:"EUR",MR:"MRU",
    MS:"XCD",MT:"EUR",MU:"MUR",MV:"MVR",MW:"MWK",MX:"MXN",MY:"MYR",MZ:"MZN",NA:"NAD",NC:"XPF",
    NE:"XOF",NF:"AUD",NG:"NGN",NI:"NIO",NL:"EUR",NO:"NOK",NP:"NPR",NR:"AUD",NU:"NZD",NZ:"NZD",
    OM:"OMR",PA:"USD",PE:"PEN",PF:"XPF",PG:"PGK",PH:"PHP",PK:"PKR",PL:"PLN",PM:"EUR",PN:"NZD",
    PR:"USD",PS:"ILS",PT:"EUR",PW:"USD",PY:"PYG",QA:"QAR",RE:"EUR",RO:"RON",RS:"RSD",RU:"RUB",
    RW:"RWF",SA:"SAR",SB:"SBD",SC:"SCR",SD:"SDG",SE:"SEK",SG:"SGD",SH:"SHP",SI:"EUR",SJ:"NOK",
    SK:"EUR",SL:"SLL",SM:"EUR",SN:"XOF",SO:"SOS",SR:"SRD",SS:"SSP",ST:"STN",SV:"USD",SX:"ANG",
    SY:"SYP",SZ:"SZL",TC:"USD",TD:"XAF",TF:"EUR",TG:"XOF",TH:"THB",TJ:"TJS",TK:"NZD",TL:"USD",
    TM:"TMT",TN:"TND",TO:"TOP",TR:"TRY",TT:"TTD",TV:"AUD",TW:"TWD",TZ:"TZS",UA:"UAH",UG:"UGX",
    UM:"USD",US:"USD",UY:"UYU",UZ:"UZS",VA:"EUR",VC:"XCD",VE:"VES",VG:"USD",VI:"USD",VN:"VND",
    VU:"VUV",WF:"XPF",WS:"WST",YE:"YER",YT:"EUR",ZA:"ZAR",ZM:"ZMW",ZW:"ZWL",
};

export default function TourismInfo({ context, loading }) {
    const { org, config, travel } = context;

    const jewishInfo = applyLang(travel?.jewishInfo || {}, context.lang);
    const tourismHtml = jewishInfo.tourismInfos;
    const eruv = jewishInfo.eruv;
    const eruvComment = jewishInfo.eruvComment;

    // Timezone + devise — résolus via Nominatim au montage
    const [timezone, setTimezone] = React.useState(null);
    const [currencyInfo, setCurrencyInfo] = React.useState(null);

    React.useEffect(() => {
        if (org.lat == null || org.long == null) return;
        fetchGeoInfo(org.lat, org.long).then(info => {
            if (!info) return;
            if (info.timezone) setTimezone(info.timezone);
            const code = CURRENCY_BY_COUNTRY[info.countryCode];
            if (code) setCurrencyInfo(findCurrencyInfo(code));
        });
    }, [org.lat, org.long]);

    // Heure locale — rafraîchie toutes les 30s via Intl (fiable)
    const [localTime, setLocalTime] = React.useState(null);
    React.useEffect(() => {
        if (!timezone) return;
        const tick = () => setLocalTime(formatTime(timezone));
        tick();
        const id = setInterval(tick, 30 * 1000);
        return () => clearInterval(id);
    }, [timezone]);

    // Chabbat réel via Hebcal
    const shabbat = React.useMemo(() => {
        if (org.lat == null || org.long == null) return null;
        return getNextShabbat(org.lat, org.long, org.israel);
    }, [org.lat, org.long, org.israel]);

    const eruvLabel = {
        yes: t`Oui`,
        no: t`Non`,
        partial: t`Partiel`,
    }[eruv];

    return (
        <div className="travel-page travel-page--tourism tr-tour">
            <style dangerouslySetInnerHTML={{ __html: tourismStyles }} />
            <Meta
                context={context}
                title={t`Informations touristiques — ${org.ville || org.name}`}
                description={config["meta.description"] || t`Heure locale, météo, érouv et infos pratiques pour les voyageurs juifs à ${org.ville || org.name}`}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">
                <div className="travel-container">

                    <h1 className="tr-tour__title">
                        <Trans>Infos tourisme à</Trans>{" "}{org.ville || org.name}
                    </h1>

                    <div className="tr-tour__subtitle">
                        <Trans>Informations <strong>voyageur</strong></Trans>
                    </div>

                    {/* ── Trois cartes ── */}
                    <div className="tr-tour__cards">

                        {/* Heure locale */}
                        <div className="tr-tour__card tr-tour__card--time">
                            <div className="tr-tour__card-icon">
                                {renderIcon({ name: "IconClock24", source: "tabler" }, 28)}
                            </div>
                            <div className="tr-tour__card-body">
                                <div className="tr-tour__card-label">
                                    <Trans>L'heure à</Trans> {org.ville || org.name}
                                </div>
                                <div className="tr-tour__card-value">
                                    {localTime || "—"}
                                </div>
                            </div>
                        </div>

                        {/* Devise locale */}
                        <div className="tr-tour__card tr-tour__card--devise">
                            <div className="tr-tour__card-icon">
                                {renderIcon({ name: "IconWallet", source: "tabler" }, 28)}
                            </div>
                            <div className="tr-tour__card-body">
                                <div className="tr-tour__card-label">
                                    <Trans>Devise</Trans>
                                </div>
                                <div className="tr-tour__card-value">
                                    {currencyInfo
                                        ? `${currencyInfo.name} ${currencyInfo.symbol}`
                                        : "—"}
                                </div>
                            </div>
                        </div>

                        {/* Horaires de Chabbat */}
                        <div className="tr-tour__card tr-tour__card--shabbat">
                            <div className="tr-tour__shabbat">
                                <div className="tr-tour__shabbat-left">
                                    <div className="tr-tour__card-icon">
                                        {renderIcon({ name: "IconCandle", source: "tabler" }, 28)}
                                    </div>
                                    <div className="tr-tour__shabbat-title">
                                        <Trans>Horaires de Chabbat</Trans>
                                    </div>
                                    {shabbat?.parasha && (
                                        <div className="tr-tour__shabbat-paracha">
                                            <Trans>Paracha</Trans> {shabbat.parasha}
                                        </div>
                                    )}
                                    {shabbat?.fridayDate && shabbat?.satDate && (
                                        <div className="tr-tour__shabbat-dates">
                                            ({shabbat.fmtDate(shabbat.fridayDate)}-{shabbat.fmtDate(shabbat.satDate)})
                                        </div>
                                    )}
                                </div>

                                <div className="tr-tour__shabbat-right">
                                    <div>
                                        <div className="tr-tour__shabbat-time-label">
                                            <Trans>Début du Chabbat</Trans>
                                        </div>
                                        <div className="tr-tour__shabbat-time-value">
                                            {shabbat?.entree ? shabbat.fmt(shabbat.entree, timezone) : "—"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="tr-tour__shabbat-time-label">
                                            <Trans>Fin du shabbat</Trans>
                                        </div>
                                        <div className="tr-tour__shabbat-time-value">
                                            {shabbat?.sortie ? shabbat.fmt(shabbat.sortie, timezone) : "—"}
                                        </div>
                                    </div>
                                </div>

                                <Link href={org.BASE_WEB_URL+"/travel/jewish-zmanim"} legacyBehavior>
                                    <a
                                        className="tr-tour__shabbat-link"
                                        style={{textDecorationLine:"underline"}}
                                    >
                                        <Trans>Horaires halakhiques à</Trans> {org.ville || org.name}
                                    </a>
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* ── Section Horaires halakhiques ── */}
                    <section id="halachiques" className="tr-tour__section">
                        <h2 className="tr-tour__section-title">
                            <Trans>Horaires Halachiques à</Trans> {org.ville || org.name}
                        </h2>
                        <p className="tr-tour__section-intro">
                            <Trans>Quand peut-on mettre les Tefillin ? Quand est le coucher du soleil ? Quand commence Chabbat ?</Trans>
                        </p>
                        <Link href={org.BASE_WEB_URL+"/travel/jewish-zmanim"} legacyBehavior>
                            <a className="tr-tour__section-link" style={{textDecorationLine:"underline"}}>
                                <Trans>Pour tous les horaires halachiques aujourd'hui à</Trans> {org.ville || org.name}
                            </a>
                        </Link>
                    </section>

                    {/* ── Eruv ── */}
                    {eruv && (
                        <div className="tr-tour__eruv">
                            <div className="tr-tour__eruv-head">
                                {renderIcon({ name: "IconFence", source: "tabler" }, 20)}
                                <Trans>Érouv</Trans>
                                <span className={`tr-tour__eruv-pill tr-tour__eruv-pill--${eruv}`}>
                                    {eruvLabel}
                                </span>
                            </div>
                            {eruvComment && (
                                <div className="tr-tour__eruv-comment">
                                    {eruvComment}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Contenu riche tourisme ── */}
                    {tourismHtml && (
                        <div
                            className="tr-tour__rich"
                            dangerouslySetInnerHTML={{ __html: tourismHtml }}
                        />
                    )}


                    <KosherPlacesGrid context={context} />
                    <HotelsList context={context} />

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
